import React, { useCallback, useState } from "react";
import { CheckCircle2 } from "lucide-react";

import { Button } from "../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { useUser } from "../../context/user-context";
import {
  DOCS,
  useDocumentUploads,
  type DocKey,
} from "../../hooks/use-document-uploads";
import DocumentUploadField from "./components/DocumentUploadField";

/**
 * Onboarding only (auth/BusinessProfile). Collects the registration documents
 * into user-context so createBusiness can submit them alongside the rest of
 * the form.
 *
 * The settings Documents tab uses the same useDocumentUploads hook but submits
 * directly — see settings/Documents.tsx.
 */
const UploadDocuments = ({ setOpen }: { setOpen?: (open: boolean) => void }) => {
  const {
    setIdentityUrl,
    setAddressUrl,
    setCacUrl,
    setUploadModal,
    bnNumber,
    setBnNumber,
    setIdFile,
    setAddyFile,
    setCacFile,
  } = useUser();

  // BusinessProfile renders this whenever `uploadModal` is set, so the dialog
  // owns its own visibility rather than being hardcoded open — otherwise
  // closing it would immediately re-open.
  const [isOpen, setIsOpen] = useState(true);

  const publishUrl = useCallback(
    (key: DocKey, objectURL: string) => {
      if (key === "identity") setIdentityUrl(objectURL);
      if (key === "address") setAddressUrl(objectURL);
      if (key === "cac") setCacUrl(objectURL);
    },
    [setIdentityUrl, setAddressUrl, setCacUrl]
  );

  const publishFile = useCallback(
    (key: DocKey, file: File | null) => {
      if (key === "identity") setIdFile(file);
      if (key === "address") setAddyFile(file);
      if (key === "cac") setCacFile(file);
    },
    [setIdFile, setAddyFile, setCacFile]
  );

  const { docs, upload, clear, allUploaded, anyUploading, uploadedCount } =
    useDocumentUploads({
      onUploaded: publishUrl,
      onFileChange: publishFile,
    });

  const close = () => {
    setIsOpen(false);
    setUploadModal(false);
    setOpen?.(false);
  };

  const canSubmit = !!bnNumber.trim() && allUploaded && !anyUploading;

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!canSubmit) return;
    close();
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(next) => {
        // Don't let a click-away interrupt an in-flight upload.
        if (!next && !anyUploading) close();
      }}
    >
      <DialogContent className="font-manrope mds:max-w-[560px]">
        <DialogHeader>
          <DialogTitle className="font-sora">
            Upload required documents
          </DialogTitle>
          <DialogDescription>
            All three documents and your RN number are required before you can
            continue.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="bn-number">RN Number</Label>
            <Input
              id="bn-number"
              type="tel"
              inputMode="numeric"
              value={bnNumber}
              onChange={(e) => setBnNumber(e.target.value)}
              placeholder="0123456789"
            />
          </div>

          {DOCS.map((doc) => (
            <DocumentUploadField
              key={doc.key}
              label={doc.label}
              hint={doc.hint}
              file={docs[doc.key].file}
              status={docs[doc.key].status}
              error={docs[doc.key].error}
              onSelect={(file) => upload(doc.key, doc.documentType, file)}
              onClear={() => clear(doc.key)}
              onRetry={() => {
                const file = docs[doc.key].file;
                if (file) upload(doc.key, doc.documentType, file);
              }}
            />
          ))}

          <div className="flex items-center gap-2 rounded-xl bg-muted/60 px-3 py-2.5">
            <CheckCircle2
              className={
                allUploaded
                  ? "size-4 shrink-0 text-success"
                  : "size-4 shrink-0 text-muted-foreground"
              }
            />
            <p className="text-[12px] font-medium text-muted-foreground">
              {uploadedCount} of {DOCS.length} documents uploaded
              {!bnNumber.trim() && " • RN number required"}
            </p>
          </div>

          {/*
            type="submit" is explicit here, and every other button in this
            form is type="button". The upload buttons used to be bare
            <button> elements inside the form, which default to type="submit"
            — so choosing a file submitted the form and fired the
            "Kindly add your BN Number" toast on every click.
          */}
          <Button
            type="submit"
            disabled={!canSubmit}
            className="h-[50px] w-full rounded-xl font-semibold"
          >
            {anyUploading ? "Uploading…" : "Done"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default UploadDocuments;
